import React from 'react';
import { DialogContent, Stack, Typography, styled } from '@mui/material';
import { Modal } from 'components/Modal/Modal';
import { CareProgramCard } from 'components/CareProgramCard/CareProgramCard';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import {
  HEALTH_TRACKER_BIOMETRIC_TYPES,
  HealthTrackerBiometricType,
  HealthTrackerProgram,
} from 'features/progress/healthTrackerTypes';
import { HealthTrackerTypeTile } from './HealthTrackerTypeTile';

const TITLE_ID = 'health-tracker-selection-title';
const MAX_TILE_WIDTH = 184;

const TYPE_LABEL_KEYS: Record<HealthTrackerBiometricType, string> = {
  weight: 'weight',
  blood_pressure: 'blood_pressure',
};

const Content = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.brandBackgroundPrimary.main,
}));

const TileGrid = styled('ul')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(2, minmax(0, ${MAX_TILE_WIDTH}px))`,
  gap: theme.spacing(2),
  listStyle: 'none',
  margin: 0,
  padding: 0,
}));

export interface HealthTrackerSelectionModalViewProps {
  open: boolean;
  onClose: () => void;
  /** Trackers listed in step 2, filtered by the selected type. */
  programs: HealthTrackerProgram[];
  /** Tracker type picked in step 1; `null` shows the type picker. */
  selectedType: HealthTrackerBiometricType | null;
  onSelectType: (type: HealthTrackerBiometricType) => void;
  /** Returns from the tracker list (step 2) to the type picker (step 1). */
  onBack: () => void;
  onSelectTracker?: (program: HealthTrackerProgram) => void;
}

export const HealthTrackerSelectionModalView: React.FC<HealthTrackerSelectionModalViewProps> = ({
  open,
  onClose,
  programs,
  selectedType,
  onSelectType,
  onBack,
  onSelectTracker,
}) => {
  const { translation } = useTranslationContext();

  const programsForType = selectedType
    ? programs.filter((program) => program.biometricType === selectedType)
    : [];

  const renderTypePicker = () => (
    <>
      <Typography variant="callout" component="h2" color="grey.800">
        {translation('health_tracker_body')}
      </Typography>
      <TileGrid>
        {HEALTH_TRACKER_BIOMETRIC_TYPES.map((type) => (
          <li key={type}>
            <HealthTrackerTypeTile
              label={translation(TYPE_LABEL_KEYS[type])}
              biometricType={type}
              onClick={() => onSelectType(type)}
            />
          </li>
        ))}
      </TileGrid>
    </>
  );

  const renderTrackerList = () => (
    <>
      <Typography variant="callout" component="h2" color="grey.800">
        {translation('health_tracker_selection')}
      </Typography>
      <Stack spacing={2} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {programsForType.map((program) => (
          <li key={program.id}>
            <CareProgramCard
              title={program.title}
              programType="Health Tracker"
              onClick={onSelectTracker ? () => onSelectTracker(program) : undefined}
              testId="health-tracker-program-card"
              // Default grey[300] shadow matches the modal background and disappears.
              sx={{ boxShadow: (theme) => `0 1px 4px 0 ${theme.palette.grey[400]}` }}
            />
          </li>
        ))}
      </Stack>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      onClickBack={selectedType ? onBack : onClose}
      overrideBackButtonVisible
      showHeader
      modalTitle={translation(selectedType ? TYPE_LABEL_KEYS[selectedType] : 'health_tracker')}
      modalTitleId={TITLE_ID}
      // Figma shows a near full-height panel on desktop.
      modalStyles={{ height: '100%' }}
      maxWidth="sm"
      fullWidth
      className="health-tracker-selection-modal"
    >
      <Content>{selectedType ? renderTrackerList() : renderTypePicker()}</Content>
    </Modal>
  );
};

export interface HealthTrackerSelectionModalProps {
  open: boolean;
  onClose: () => void;
  programs: HealthTrackerProgram[];
  onSelectTracker?: (program: HealthTrackerProgram) => void;
}

/** Owns the step state (type picker → tracker list) for the Browse Trackers flow. */
export const HealthTrackerSelectionModal: React.FC<HealthTrackerSelectionModalProps> = ({
  open,
  onClose,
  programs,
  onSelectTracker,
}) => {
  const [selectedType, setSelectedType] = React.useState<HealthTrackerBiometricType | null>(null);
  const [wasOpen, setWasOpen] = React.useState(open);

  // Restart at the type picker each time the flow is opened.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedType(null);
    }
  }

  return (
    <HealthTrackerSelectionModalView
      open={open}
      onClose={onClose}
      programs={programs}
      selectedType={selectedType}
      onSelectType={setSelectedType}
      onBack={() => setSelectedType(null)}
      onSelectTracker={onSelectTracker}
    />
  );
};
